import { useState, useRef, useCallback, useEffect } from 'react';
import * as Tone from 'tone';
import { SwingUtils } from '../utils/swingUtils';
import { RepeatAnalyzer } from '../utils/repeatAnalyzer';

// Helper function to determine if a note should be included for audio playback based on divisi voice settings
const shouldIncludeNoteForAudio = (noteEvent, partControls, divisiAnalysis, partToInstrumentMapRef = null) => {
  // Always include if missing critical data
  if (!noteEvent || !partControls) return true;
  if (!divisiAnalysis || !divisiAnalysis.instances) return true;
  
  // Find the part control for this note
  // CRITICAL FIX: noteEvent.partId is the original partId, but partControls use instrumentId
  // We need to map partId to instrumentId to find the right control
  let partControl = null;
  
  if (partToInstrumentMapRef?.current) {
    const instrumentId = partToInstrumentMapRef.current[noteEvent.partId];
    partControl = partControls.find(part => part.id.toString() === instrumentId?.toString());
    
  }
  
  // Fallback: try direct match
  if (!partControl) {
    partControl = partControls.find(part => part.id.toString() === noteEvent.partId.toString());
  }
  
  if (!partControl) return true;
  
  // Check if this part has divisi at all
  const partHasDivisi = divisiAnalysis.instances.some(d => d.partId.toString() === noteEvent.partId.toString());
  if (!partHasDivisi) {
    // Part has no divisi, include all notes
    return true;
  }
  
  // Part has divisi - check if this specific note is part of any divisi instance
  const divisiInstance = divisiAnalysis.instances.find(divisi => {
    if (divisi.partId.toString() !== noteEvent.partId.toString()) return false;
    
    // Check if this note matches either voice in the divisi
    const matchesUpper = divisi.upperVoice && 
      Math.abs(noteEvent.startTime - divisi.upperVoice.startTime) < 0.05 &&
      noteEvent.pitch === divisi.upperVoice.pitch;
      
    const matchesLower = divisi.lowerVoice &&
      Math.abs(noteEvent.startTime - divisi.lowerVoice.startTime) < 0.05 &&
      noteEvent.pitch === divisi.lowerVoice.pitch;
      
    return matchesUpper || matchesLower;
  });
  
  // If this note is NOT part of any specific divisi instance, include it
  // (these are regular notes in a part that also has some divisi sections)
  if (!divisiInstance) return true;
  
  // This note IS part of a divisi instance - determine which voice and check if enabled
  const isUpperVoice = divisiInstance.upperVoice && 
    Math.abs(noteEvent.startTime - divisiInstance.upperVoice.startTime) < 0.05 &&
    noteEvent.pitch === divisiInstance.upperVoice.pitch;
  
  const isLowerVoice = divisiInstance.lowerVoice &&
    Math.abs(noteEvent.startTime - divisiInstance.lowerVoice.startTime) < 0.05 &&
    noteEvent.pitch === divisiInstance.lowerVoice.pitch;
  
  // Determine if this divisi note should be included based on voice settings
  if (isUpperVoice) {
    return partControl.upperVoice !== false;
  } else if (isLowerVoice) {
    return partControl.lowerVoice !== false;
  }
  
  // Safety fallback - if we can't determine the voice, include it
  return true;
};

export const useScoreParser = (synthManager, isSwingMode = false, swingRatio = 5/8, velocity = 0.5) => {
  const [partControls, setPartControls] = useState([]);
  const [pendingVisibilitySync, setPendingVisibilitySync] = useState(false);
  const [respectRepeats, setRespectRepeats] = useState(true);
  
  const currentTempoBPMRef = useRef(120);
  const instrumentToPartMapRef = useRef({});
  const partToInstrumentMapRef = useRef({});
  const allNotesWithTimingRef = useRef([]);
  const originalNotesWithTimingRef = useRef([]); // Store original notes before repeat expansion
  const originalNoteEventsRef = useRef([]); // Store original audio events before repeat expansion
  const repeatAnalysisRef = useRef(null); // Store repeat analysis results
  const osmdRef = useRef(null); // Store OSMD reference for re-rendering
  const partControlsRef = useRef([]); // Store current part controls

  const createPartControls = useCallback((osmd) => {
    osmdRef.current = osmd; // Store reference
    const newPartControls = [];
    
    if (!osmd?.Sheet || !osmd?.Sheet.Instruments) {
      setPartControls([]);
      return;
    }

    // Get existing part controls to preserve settings during re-creation
    const existingPartControls = partControlsRef.current || [];
    
    // Safari mobile fix: Define outside the loop
    const isSafariMobile = /Safari/.test(navigator.userAgent) && /Mobile/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    

    osmd.Sheet.Instruments.forEach((instrumentObj, index) => {
      const instrumentId = instrumentObj.Id;
      const partName = instrumentObj.NameLabel ? instrumentObj.NameLabel.text : `Part ${index + 1}`;
      
      // Find existing settings for this part to preserve them
      const existingPart = existingPartControls.find(part => part.id === instrumentId);
      
      newPartControls.push({
        id: instrumentId,
        name: partName,
        checked: existingPart?.checked ?? true,    // Preserve existing or default to enabled
        visible: existingPart?.visible ?? true,    // Preserve existing or default to visible
        upperVoice: existingPart?.upperVoice ?? true, // Preserve existing or default to enabled
        lowerVoice: existingPart?.lowerVoice ?? true  // Preserve existing or default to enabled
      });
    });
    
    
    setPartControls(newPartControls);
    partControlsRef.current = newPartControls; // Keep ref in sync
    
    
    // Safari mobile fix: Store visibility state as backup
    if (isSafariMobile) {
      window._lastKnownPartVisibility = {};
      newPartControls.forEach(part => {
        window._lastKnownPartVisibility[part.id] = part.visible;
      });
    }
    
    // Set a flag that parts have been created - this will trigger visibility sync if needed
    osmd._partsReady = true;
  }, []);

  // Detect swing tags in MusicXML and parse the ratio if present
  const detectSwing = useCallback((osmd) => {
    try {
      // Check the globally stored MusicXML content
      if (window._currentMusicXMLContent && typeof window._currentMusicXMLContent === 'string') {
        const xmlContent = window._currentMusicXMLContent;

        if (xmlContent.includes('<swing>')) {
          // Try to parse the swing ratio from the first <swing> element
          // MusicXML format: <swing><first>N</first><second>M</second><swing-type>eighth|16th</swing-type></swing>
          // Or: <swing><straight/></swing> for no swing

          // Check for <straight> first - means swing is explicitly disabled
          const straightMatch = xmlContent.match(/<swing>\s*<straight\s*\/>/i);
          if (straightMatch) {
            return { hasSwing: false, swingRatio: null, fromFile: true };
          }

          // Parse first and second values
          const firstMatch = xmlContent.match(/<swing>[\s\S]*?<first>(\d+)<\/first>/i);
          const secondMatch = xmlContent.match(/<swing>[\s\S]*?<second>(\d+)<\/second>/i);
          const swingTypeMatch = xmlContent.match(/<swing>[\s\S]*?<swing-type>(eighth|16th)<\/swing-type>/i);

          if (firstMatch && secondMatch) {
            const first = parseInt(firstMatch[1], 10);
            const second = parseInt(secondMatch[1], 10);

            if (first > 0 && second > 0) {
              // Calculate ratio as first / (first + second)
              const ratio = first / (first + second);
              const swingType = swingTypeMatch ? swingTypeMatch[1] : 'eighth';

              return {
                hasSwing: true,
                swingRatio: ratio,
                swingFirst: first,
                swingSecond: second,
                swingType: swingType,
                fromFile: true
              };
            }
          }

          // Has <swing> tag but couldn't parse ratio - return true with no ratio
          return { hasSwing: true, swingRatio: null, fromFile: true };
        }
      }

    } catch (error) {
      // Silent error handling
    }

    return { hasSwing: false, swingRatio: null, fromFile: false };
  }, []);

  // Enhanced tempo detection with support for different note types (e.g., half note = 80)
  const detectTempo = useCallback((osmd) => {
    let detectedBPM = 120; // Default fallback

    // Playback runs on quarter notes. <sound tempo> is quarter-note BPM by
    // definition; a <metronome> mark is in its own beat unit, so "half = 60"
    // means 120 and "dotted quarter = 60" means 90.
    const xml = window._currentMusicXMLContent;
    if (typeof xml === 'string') {
      const sound = xml.match(/<sound[^>]*\btempo="([\d.]+)"/i);
      if (sound && parseFloat(sound[1]) > 0) {
        return Math.round(parseFloat(sound[1]));
      }
      const mark = xml.match(/<metronome[^>]*>\s*<beat-unit>([a-z0-9]+)<\/beat-unit>\s*(<beat-unit-dot\s*\/>)?[\s\S]*?<per-minute>([\d.]+)<\/per-minute>/i);
      if (mark) {
        const unitQuarters = { whole: 4, half: 2, quarter: 1, eighth: 0.5, '16th': 0.25 }[mark[1].toLowerCase()];
        const perMinute = parseFloat(mark[3]);
        if (unitQuarters && perMinute > 0) {
          return Math.round(perMinute * unitQuarters * (mark[2] ? 1.5 : 1));
        }
      }
    }

    try {
      // First, try to find tempo markings in the score
      if (osmd?.Sheet?.sourceMeasures) {
        for (let i = 0; i < Math.min(5, osmd.Sheet.sourceMeasures.length); i++) {
          const measure = osmd.Sheet.sourceMeasures[i];
          
          if (measure?.staffEntries) {
            for (const staffEntry of measure.staffEntries) {
              if (staffEntry?.instructions) {
                for (const instruction of staffEntry.instructions) {
                  // Check for metronome markings with type and BPM
                  if (instruction?.type === "MetronomeMark" && instruction?.tempoInBPM) {
                    // Trust OSMD's tempoInBPM, as it's already normalized to quarter notes
                    return Math.round(instruction.tempoInBPM);
                  }

                  // Fallback for older OSMD versions or different structures
                  if (instruction?.TempoInBpm) {
                    return Math.round(instruction.TempoInBpm);
                  }
                }
              }
            }
          }
        }
      }

      // If no metronome mark is found, try the default tempo from OSMD
      if (osmd?.Sheet?.DefaultStartTempoInBpm) {
        return Math.round(osmd.Sheet.DefaultStartTempoInBpm);
      }

    } catch (error) {
      console.error("Error detecting tempo:", error);
    }
    
    return Math.round(detectedBPM);
  }, []);

  const preparePlayback = useCallback(async (osmd, audioHook) => {
    // Get current partControls from ref (always up to date)
    const currentPartControls = partControlsRef.current;
    
    // Detect swing tags in MusicXML
    const swingInfo = detectSwing(osmd);
    if (swingInfo.hasSwing) {
      // Store swing detection result to be used later
      osmd._hasSwingDetected = true;
    }
    
    const noteEvents = [];
    const allNotesWithTiming = [];
    audioHook.clearScheduledEvents();
    
    if (!osmd?.cursor || !osmd?.Sheet || !osmd?.Sheet.Instruments) {
      throw new Error("Essential score objects not ready for playback preparation.");
    }

    // Enhanced tempo detection
    const originalBPM = detectTempo(osmd);
    currentTempoBPMRef.current = originalBPM;

    // Active transposition in half steps. OSMD's TransposeCalculator only transposes
    // the rendered notation; source note pitches stay original, so audio must apply
    // the same offset itself.
    const sheetTranspose = osmd.Sheet.Transpose || 0;
    
    // Apply playback speed to Transport BPM
    const effectiveBPM = originalBPM * audioHook.playbackSpeed;
    Tone.Transport.bpm.value = effectiveBPM;
    // Register the BPM these events are baked at, resetting any live tempo adjustment
    if (audioHook.setPreparedBPM) {
      audioHook.setPreparedBPM(effectiveBPM);
    }

    let notesPerPart = {};
    let discoveredParts = new Set();

    // Track voice entries by measure and timestamp for divisi detection
    const measureVoiceEntries = new Map(); // measureNumber -> timestamp -> voiceEntries[]
    const divisiDetections = [];

    // Walk the score with every instrument temporarily visible so hidden parts
    // still yield audio events. The walk is synchronous and the flags are restored
    // in `finally` before the first await below, so no concurrent render or
    // visibility toggle can observe (or be clobbered by) the forced state.
    const originalVisibilityState = {};
    osmd.Sheet.Instruments.forEach(instrument => {
      originalVisibilityState[instrument.Id] = instrument.Visible;
      instrument.Visible = true;
    });
    try {
      osmd.cursor.reset();
      const cursorIterator = osmd.cursor.Iterator;

      while (cursorIterator && !cursorIterator.EndReached) {
        const voiceEntries = cursorIterator.CurrentVoiceEntries;
        let currentEventTimestampValue;

        if (cursorIterator.CurrentTimestamp) {
          currentEventTimestampValue = cursorIterator.CurrentTimestamp.RealValue;
        } else if (cursorIterator.currentTimeStamp) {
          currentEventTimestampValue = cursorIterator.currentTimeStamp.RealValue;
        } else { 
          osmd.cursor.next(); 
          continue; 
        }
      
        // Use effective BPM (with speed applied) for time calculations
              const startTimeSeconds = (currentEventTimestampValue * 4) * (60.0 / effectiveBPM);
      
        // Track voice entries for divisi detection
        const currentMeasureNumber = cursorIterator.CurrentMeasure?.MeasureNumber || 0;
        if (!measureVoiceEntries.has(currentMeasureNumber)) {
          measureVoiceEntries.set(currentMeasureNumber, new Map());
        }
        const measureMap = measureVoiceEntries.get(currentMeasureNumber);
        const timestampKey = currentEventTimestampValue;
        if (!measureMap.has(timestampKey)) {
          measureMap.set(timestampKey, []);
        }
        if (voiceEntries) {
          measureMap.get(timestampKey).push(...voiceEntries);
        }

        if (voiceEntries) {
          voiceEntries.forEach((voiceEntry) => {
            let instrumentForVoiceEntry = null;
          
            // Find the instrument for this voice entry
            if (voiceEntry.ParentVoice?.Parent) {
              instrumentForVoiceEntry = voiceEntry.ParentVoice.Parent;
            } else if (voiceEntry.ParentInstrument) {
              instrumentForVoiceEntry = voiceEntry.ParentInstrument;
            } else if (voiceEntry.parentSourceStaffEntry?.ParentInstrument) {
              instrumentForVoiceEntry = voiceEntry.parentSourceStaffEntry.ParentInstrument;
            } else if (voiceEntry.ParentStaff?.ParentInstrument) {
              instrumentForVoiceEntry = voiceEntry.ParentStaff.ParentInstrument;
            }
          
            const partIdForNotesInThisVoiceEntry = instrumentForVoiceEntry ? instrumentForVoiceEntry.Id : null;

            if (partIdForNotesInThisVoiceEntry === null) {
              return;
            }

            // Track voice information for divisi detection
            const voiceId = voiceEntry.ParentVoice?.VoiceId || voiceEntry.ParentVoice?.Id || 0;
            const voiceInfo = {
              instrumentId: partIdForNotesInThisVoiceEntry,
              voiceId: voiceId
            };

            discoveredParts.add(partIdForNotesInThisVoiceEntry);

            if (voiceEntry.notes) {
              voiceEntry.notes.forEach((note) => {
                // Use effective BPM for duration calculations too
                let durationSeconds = (note.Length.RealValue * 4) * (60.0 / effectiveBPM);
                if (durationSeconds <= 0.001) durationSeconds = 0.001;
              
                const endTimeSeconds = startTimeSeconds + durationSeconds;
              
                if (note.isRest()) {
                  // Track rests too
                  allNotesWithTiming.push({
                    startTime: startTimeSeconds,
                    endTime: endTimeSeconds,
                    partId: partIdForNotesInThisVoiceEntry,
                    voiceId: voiceId,
                    isRest: true,
                    pitch: 'REST',
                    sourceNote: note
                  });
                } else {
                  const isPlayableCandidate = !note.isRest();
                  const hasPitchObject = !!note.pitch;
                  let finalMidiNumber = null;

                  if (hasPitchObject) {
                    if (typeof note.pitch.midiNumber === 'number' && isFinite(note.pitch.midiNumber)) {
                      finalMidiNumber = note.pitch.midiNumber;
                    } else if (typeof note.pitch.getMidiNumber === 'function') {
                      const m = note.pitch.getMidiNumber();
                      if (typeof m === 'number' && isFinite(m)) finalMidiNumber = m;
                    } else if (typeof note.pitch.halfTone === 'number' && isFinite(note.pitch.halfTone)) {
                      finalMidiNumber = note.pitch.halfTone;
                    }
                  }
                
                  if (isPlayableCandidate && (finalMidiNumber !== null)) {
                    // Transpose up by 12 semitones (1 octave) to fix audio playing too low,
                    // plus the user-selected key transposition
                    const transposedMidiNumber = finalMidiNumber + 12 + sheetTranspose;

                    // Validate final MIDI number range
                    if (transposedMidiNumber < 0 || transposedMidiNumber > 127) {
                      return;
                    }

                    const calculatedPitch = Tone.Frequency(transposedMidiNumber, "midi").toNote();
                  

                    if (typeof calculatedPitch === 'string' && !calculatedPitch.includes("undefined") && isFinite(durationSeconds) && durationSeconds > 0) {
                    
                      // Check for actual tie markings in the note object
                      let isTieStart = false;
                      let isTieEnd = false;
                    
                      // Check for actual OSMD tie property
                      if (note.tie && note.tie.notes && note.tie.notes.length >= 2) {
                        const firstNote = note.tie.notes[0];
                      
                        if (note === firstNote) {
                          // This is the first note in a tie - keep it and extend its duration
                          const totalDuration = note.tie.notes.reduce((sum, tiedNote) => {
                            return sum + (tiedNote.Length?.RealValue || 0) * 4 * (60.0 / effectiveBPM);
                          }, 0);
                        
                          durationSeconds = totalDuration;
                          isTieStart = true;
                        
                          // Create the single playback event
                          noteEvents.push({
                            time: startTimeSeconds,
                            pitch: calculatedPitch,
                            duration: durationSeconds,
                            velocity: velocity,
                            partId: partIdForNotesInThisVoiceEntry,
                            isTieStart,
                            isTieEnd,
                            sourceNote: note
                          });
                        
                          // Create highlighting entries for ALL tied notes
                          let currentTime = startTimeSeconds;
                          note.tie.notes.forEach((tiedNote, index) => {
                            const noteDuration = (tiedNote.Length?.RealValue || 0) * 4 * (60.0 / effectiveBPM);
                          
                            allNotesWithTiming.push({
                              startTime: currentTime,
                              endTime: currentTime + noteDuration,
                              partId: partIdForNotesInThisVoiceEntry,
                              voiceId: voiceId,
                              isRest: false,
                              pitch: calculatedPitch,
                              sourceNote: tiedNote, // Use the actual tied note for highlighting
                              isTieStart: index === 0,
                              isTieEnd: index === note.tie.notes.length - 1
                            });
                          
                            currentTime += noteDuration;
                          });
                        
                          return; // Done processing this tie group
                        } else {
                          // This is a continuation note - skip it entirely
                          return; // Don't create any events for this note
                        }
                      }
                    
                      // Regular note (not tied)
                      noteEvents.push({
                        time: startTimeSeconds,
                        pitch: calculatedPitch,
                        duration: durationSeconds,
                        velocity: velocity,
                        partId: partIdForNotesInThisVoiceEntry,
                        isTieStart,
                        isTieEnd,
                        sourceNote: note
                      });
                    
                      // Track for display and highlighting
                      allNotesWithTiming.push({
                        startTime: startTimeSeconds,
                        endTime: endTimeSeconds,
                        partId: partIdForNotesInThisVoiceEntry,
                        voiceId: voiceId,
                        isRest: false,
                        pitch: calculatedPitch,
                        sourceNote: note,
                        isTieStart,
                        isTieEnd
                      });
                    
                      notesPerPart[partIdForNotesInThisVoiceEntry] = (notesPerPart[partIdForNotesInThisVoiceEntry] || 0) + 1;
                    }
                  }
                }
              });
            } else {
              // No notes in this voice entry - might be a rest measure
              let durationSeconds = (voiceEntry.Length?.RealValue || 1) * 4 * (60.0 / effectiveBPM);
              if (durationSeconds <= 0.001) durationSeconds = 0.001;
            
              allNotesWithTiming.push({
                startTime: startTimeSeconds,
                endTime: startTimeSeconds + durationSeconds,
                partId: partIdForNotesInThisVoiceEntry,
                voiceId: voiceId,
                isRest: true,
                pitch: 'REST',
                sourceNote: null
              });
            }
          });
        }
        osmd.cursor.next();
      }
    } finally {
      osmd.Sheet.Instruments.forEach(instrument => {
        instrument.Visible = originalVisibilityState[instrument.Id];
      });
    }
    
    
    // Detect divisi after parsing all measures
    detectDivisi(noteEvents);
    
    // Apply divisi filtering to note events before scheduling
    const filteredNoteEvents = applyDivisiFiltering([...noteEvents], currentPartControls); // Create copy to avoid reference issues
    
    // Update noteEvents array with filtered results
    noteEvents.length = 0;
    noteEvents.push(...filteredNoteEvents);
    
    // Store original notes with timing (before repeat expansion)
    const originalNotesWithTiming = allNotesWithTiming.sort((a, b) => a.startTime - b.startTime);
    originalNotesWithTimingRef.current = [...originalNotesWithTiming];

    // Store original audio events (before repeat expansion) for loop mode
    originalNoteEventsRef.current = [...noteEvents];

    // Analyze repeats and create expanded sequence if needed
    let finalNotesWithTiming = originalNotesWithTiming;
    let finalNoteEvents = [...noteEvents];
    
    // Store the XML content in OSMD for repeat analysis
    if (osmd && !osmd._xmlString) {
      osmd._xmlString = window._currentMusicXMLContent;
    }
    
    const repeatAnalysis = RepeatAnalyzer.analyzeRepeats(osmd);
    repeatAnalysisRef.current = repeatAnalysis;
    
    if (repeatAnalysis.hasRepeats && respectRepeats) {
      
      // Build measure timing map for repeat expansion
      const measureTimingMap = buildMeasureTimingMapForRepeats(osmd, originalNotesWithTiming);
      
      // Create expanded note sequence following repeat structure
      const expandedNotes = RepeatAnalyzer.createExpandedNoteSequence(
        originalNotesWithTiming,
        repeatAnalysis.playbackOrder,
        measureTimingMap
      );
      
      // Create expanded audio events - need to adapt audio events format
      const adaptedAudioEvents = noteEvents.map(event => ({
        ...event,
        startTime: event.time, // Audio events use 'time', visual notes use 'startTime'
        endTime: event.time + (event.duration || 0)
      }));
      
      const expandedAudioEvents = RepeatAnalyzer.createExpandedNoteSequence(
        adaptedAudioEvents,
        repeatAnalysis.playbackOrder,
        measureTimingMap
      ).map(event => ({
        ...event,
        time: event.startTime, // Convert back to audio format
        duration: event.endTime - event.startTime
      }));
      
      // Safety check: if expansion failed, fall back to original
      if (expandedAudioEvents.length === 0) {
        console.warn('⚠️ Audio expansion failed, using original events');
        finalNoteEvents = [...noteEvents];
      } else {
        finalNoteEvents = expandedAudioEvents;
        
      }
      
      finalNotesWithTiming = expandedNotes;
      
    }
    
    // Store final notes with timing (highlighting uses this)
    allNotesWithTimingRef.current = finalNotesWithTiming;
    
    // Update audio events with final sequence
    noteEvents.length = 0;
    noteEvents.push(...finalNoteEvents);
    
    // Create part mappings
    const sortedInstruments = [...osmd.Sheet.Instruments].sort((a, b) => a.Id - b.Id);
    const sortedParts = Array.from(discoveredParts).sort((a, b) => a - b);
    
    if (sortedParts.length === sortedInstruments.length && 
        sortedParts[0] === sortedInstruments[0].Id + 1) {
      sortedInstruments.forEach((instrumentObj, index) => {
        const partId = instrumentObj.Id + 1;
        instrumentToPartMapRef.current[instrumentObj.Id] = partId;
        partToInstrumentMapRef.current[partId] = instrumentObj.Id;
      });
    } else {
      sortedInstruments.forEach((instrumentObj, index) => {
        if (index < sortedParts.length) {
          instrumentToPartMapRef.current[instrumentObj.Id] = sortedParts[index];
          partToInstrumentMapRef.current[sortedParts[index]] = instrumentObj.Id;
        }
      });
    }
    
    
    // Create synths for discovered parts
    for (const partId of discoveredParts) {
      await synthManager.createOrUpdatePartSynth(partId, true);
    }
    
    // Sort note events by time for processing
    noteEvents.sort((a, b) => a.time - b.time);

    // Tie processing is now handled during note creation, so no post-processing needed

    // Final deduplication for any remaining duplicates
    if (noteEvents.length > 0) {
      const uniqueNoteEvents = [];
      
      for (let i = 0; i < noteEvents.length; i++) {
        const currentEvent = noteEvents[i];
        
        // Check if this is a duplicate of a previous event
        // Use stricter tolerance for repeated sections to avoid removing legitimate repeated notes
        const tolerance = currentEvent.isRepeated ? 0.0001 : 0.001;
        const isDuplicate = uniqueNoteEvents.some(existingEvent => 
          Math.abs(currentEvent.time - existingEvent.time) < tolerance &&
          currentEvent.pitch === existingEvent.pitch &&
          currentEvent.partId === existingEvent.partId &&
          // Additional check: don't consider repeated notes as duplicates of originals
          !(currentEvent.isRepeated && !existingEvent.isRepeated)
        );
        
        if (!isDuplicate) {
          uniqueNoteEvents.push(currentEvent);
        }
      }
      
      noteEvents.length = 0;
      noteEvents.push(...uniqueNoteEvents);

    }

    // Apply swing timing if swing mode is enabled
    if (isSwingMode && noteEvents.length > 0) {
      // console.log('==== APPLYING SWING TIMING ====');
      // console.log(`Original BPM: ${currentTempoBPMRef.current}, Note events: ${noteEvents.length}`);
      // console.log(`allNotesWithTiming length: ${allNotesWithTiming.length}`);
      
      // Use the known effective BPM directly instead of trying to detect it from note patterns
      const beatDuration = 60 / effectiveBPM;
      
      // Apply swing to audio events using the configured swing ratio
      const swungEvents = SwingUtils.applySwingTiming(noteEvents, 4, beatDuration, swingRatio, false);

      // Apply swing to visual highlight events as well
      if (allNotesWithTiming.length > 0) {
        // console.log('Applying swing to visual events...');
        const swungVisualEvents = SwingUtils.applySwingTiming(allNotesWithTiming, 4, beatDuration, swingRatio, false);
        
        // Debug: Check if visual events have swing info
        const visualSwungCount = swungVisualEvents.filter(note => note.isSwung).length;
        // console.log(`Visual events: ${visualSwungCount} of ${swungVisualEvents.length} marked as swung`);
        
        // Update visual array
        allNotesWithTiming.length = 0;
        allNotesWithTiming.push(...swungVisualEvents);
      } else {
        // console.log('WARNING: allNotesWithTiming is empty, cannot apply swing to visual events');
      }
      
      // Always show the swing changes summary  
      if (SwingUtils.hasSignificantSwingChanges(noteEvents, swungEvents)) {
        // console.log('✓ Swing timing applied with significant changes');
        SwingUtils.debugSwingChanges(noteEvents, swungEvents, 5);
      } else {
        // console.log('ℹ Swing timing applied but no significant changes detected');
      }
      
      // console.log(`🎵 SWING SUMMARY: Successfully modified timing for better swing feel!`);
      
      // Update audio events array
      noteEvents.length = 0;
      noteEvents.push(...swungEvents);
      
      // console.log('==== SWING TIMING COMPLETE ====');
    } else if (isSwingMode) {
      // console.log('Swing mode enabled but no note events to process');
    } else if (isSwingMode && allNotesWithTiming.length > 0) {
      // console.log('Swing mode enabled, have visual notes but no audio events?');
    }

    // Store filtered note events in audioHook (already filtered above)
    audioHook.setNoteEvents(noteEvents);
    
    // Clear divisi announcement tracking for fresh playback
    if (osmdRef.current) {
      osmdRef.current._lastDivisiAnnouncements = new Set();
    }
    
    // Schedule the events
    audioHook.scheduleNoteEvents();
  }, [synthManager, detectTempo, isSwingMode]);

  // Helper function to build measure timing map for repeat expansion
  const buildMeasureTimingMapForRepeats = useCallback((osmd, notesWithTiming) => {
    const measureTimingMap = new Map();
    
    if (!osmd?.Sheet?.sourceMeasures || !notesWithTiming.length) {
      return measureTimingMap;
    }

    // Group notes by measure number if available from source
    const notesByMeasure = new Map();
    
    notesWithTiming.forEach(note => {
      let measureNumber = null;
      
      // Try to get measure number from source note
      if (note.sourceNote) {
        measureNumber = extractMeasureNumberFromSourceNote(note.sourceNote);
      }
      
      // Fallback to measure 1 if we can't determine
      if (measureNumber === null) {
        measureNumber = 1;
      }
      
      if (!notesByMeasure.has(measureNumber)) {
        notesByMeasure.set(measureNumber, []);
      }
      notesByMeasure.get(measureNumber).push(note);
    });

    // Calculate timing boundaries for each measure
    notesByMeasure.forEach((notes, measureNumber) => {
      if (notes.length > 0) {
        const startTime = Math.min(...notes.map(n => n.startTime));
        const endTime = Math.max(...notes.map(n => n.endTime));

        // Ensure measure has minimum duration to prevent timing issues
        const minimumDuration = 0.1; // 100ms minimum
        const adjustedEndTime = Math.max(endTime, startTime + minimumDuration);

        measureTimingMap.set(measureNumber, {
          startTime,
          endTime: adjustedEndTime,
          noteCount: notes.length,
          // Debug info
          _debug: {
            originalEndTime: endTime,
            adjustedEndTime: adjustedEndTime,
            duration: adjustedEndTime - startTime
          }
        });
      }
    });

    // PICKUP FIX: Add timing for measure 0 if it exists in OSMD but has no notes
    // (pickup notes are often misassigned to measure 1)
    if (!measureTimingMap.has(0) && osmd.Sheet.sourceMeasures.length > 0) {
      const firstMeasure = osmd.Sheet.sourceMeasures[0];
      if (firstMeasure && firstMeasure.MeasureNumber === 0) {
        // Measure 0 exists but has no notes - it's a pickup measure
        // Calculate its duration from the OSMD measure data
        const measureDuration = firstMeasure.Duration?.RealValue || 0;
        const timeSignature = firstMeasure.ActiveTimeSignature?.RealValue || 1;

        if (measureDuration > 0 && measureDuration < timeSignature) {
          // This is a partial measure (pickup)
          measureTimingMap.set(0, {
            startTime: 0,
            endTime: measureDuration,
            noteCount: 0,
            _debug: {
              isPickup: true,
              duration: measureDuration,
              timeSignature: timeSignature
            }
          });

          console.log(`🎵 Added measure 0 timing (pickup): 0 to ${measureDuration}`);
        }
      }
    }

    return measureTimingMap;
  }, []);

  // Helper function to extract measure number from source note
  const extractMeasureNumberFromSourceNote = useCallback((sourceNote) => {
    try {
      // Try various paths to find measure number
      if (sourceNote.sourceMeasure?.MeasureNumber) {
        return sourceNote.sourceMeasure.MeasureNumber;
      }
      
      if (sourceNote.parentStaffEntry?.ParentMeasure?.MeasureNumber) {
        return sourceNote.parentStaffEntry.ParentMeasure.MeasureNumber;
      }
      
      if (sourceNote.ParentVoiceEntry?.ParentStaffEntry?.ParentMeasure?.MeasureNumber) {
        return sourceNote.ParentVoiceEntry.ParentStaffEntry.ParentMeasure.MeasureNumber;
      }
      
      return null;
    } catch (error) {
      return null;
    }
  }, []);

  // Proper divisi detection with accurate time signature and rest handling
  const detectDivisi = useCallback((noteEvents) => {
    // console.log('🎼 DIVISI DETECTION - First 10 Measures:');
    // console.log('==========================================');
    
    const allNotes = allNotesWithTimingRef.current;
    if (!allNotes || allNotes.length === 0) {
      // console.log('No notes found.');
      return;
    }
    
    // Get OSMD reference to access time signature data
    const osmd = osmdRef.current;
    if (!osmd?.Sheet?.sourceMeasures) {
      // console.log('OSMD data not available for time signature detection.');
      return;
    }
    
    // console.log(`Total notes/rests found: ${allNotes.length}`);
    
    // CORRECT METHOD: Use measure duration to detect time signatures per OSMD documentation
    const timeSignatures = new Map(); // measureNumber -> {numerator, denominator}
    
    // console.log('🔍 DETECTING TIME SIGNATURES USING MEASURE DURATION METHOD:');
    
    // Function to convert measure duration to time signature
    const inferTimeSignatureFromDuration = (duration) => {
      // Based on OSMD documentation:
      // 4/4 = 1.0, 3/4 = 0.75, 2/4 = 0.5, 6/8 = 0.75, etc.
      if (Math.abs(duration - 1.0) < 0.01) {
        return { numerator: 4, denominator: 4 };
      } else if (Math.abs(duration - 0.75) < 0.01) {
        // Could be 3/4 or 6/8 - we'll assume 3/4 for now
        return { numerator: 3, denominator: 4 };
      } else if (Math.abs(duration - 0.5) < 0.01) {
        return { numerator: 2, denominator: 4 };
      } else if (Math.abs(duration - 1.5) < 0.01) {
        return { numerator: 6, denominator: 4 };
      } else if (Math.abs(duration - 0.375) < 0.01) {
        return { numerator: 3, denominator: 8 };
      } else {
        // console.log(`Unknown duration ${duration}, defaulting to 4/4`);
        return { numerator: 4, denominator: 4 };
      }
    };
    
    // Reset cursor to beginning
    osmd.cursor.reset();
    
    // Check time signatures for all measures
    for (let measureNum = 1; measureNum <= Math.min(50, osmd.Sheet.sourceMeasures.length); measureNum++) {
      try {
        if (osmd.cursor?.Iterator?.CurrentMeasure?.Duration?.RealValue !== undefined) {
          const duration = osmd.cursor.Iterator.CurrentMeasure.Duration.RealValue;
          const timeSignature = inferTimeSignatureFromDuration(duration);
          timeSignatures.set(measureNum, timeSignature);
          
          // console.log(`Measure ${measureNum}: Duration=${duration.toFixed(3)} → ${timeSignature.numerator}/${timeSignature.denominator}`);
        } else {
          // console.log(`Measure ${measureNum}: No duration available, using 4/4`);
          timeSignatures.set(measureNum, { numerator: 4, denominator: 4 });
        }
        
        // Move to next measure
        if (measureNum < 10) {
          // Move cursor to next measure
          let moveAttempts = 0;
          const currentMeasureNum = osmd.cursor.Iterator.CurrentMeasure?.MeasureNumber || measureNum;
          
          while (moveAttempts < 50 && (osmd.cursor.Iterator.CurrentMeasure?.MeasureNumber || measureNum) === currentMeasureNum) {
            if (osmd.cursor.Iterator.EndReached) break;
            osmd.cursor.next();
            moveAttempts++;
          }
        }
      } catch (error) {
        // console.log(`Error detecting time signature for measure ${measureNum}:`, error.message);
        timeSignatures.set(measureNum, { numerator: 4, denominator: 4 });
      }
    }
    
    // Calculate measure boundaries using actual detected time signatures
    const measureBoundaries = new Map(); // measureNumber -> {startTime, endTime, beatsPerMeasure}
    let currentTime = 0;
    
    // console.log('\n📏 CALCULATING MEASURE BOUNDARIES:');
    
    for (let measureNum = 1; measureNum <= Math.min(50, osmd.Sheet.sourceMeasures.length); measureNum++) {
      const ts = timeSignatures.get(measureNum) || { numerator: 4, denominator: 4 };
      const beatsPerMeasure = ts.numerator;
      
      // Calculate measure duration: (beats_per_measure * 60) / BPM
      const measureDurationSeconds = (beatsPerMeasure * 60) / (currentTempoBPMRef.current || 120);
      
      measureBoundaries.set(measureNum, {
        startTime: currentTime,
        endTime: currentTime + measureDurationSeconds,
        beatsPerMeasure: beatsPerMeasure,
        timeSignature: ts,
        measureDurationSeconds: measureDurationSeconds
      });
      
      // console.log(`Measure ${measureNum}: ${ts.numerator}/${ts.denominator} → ${measureDurationSeconds.toFixed(2)}s (${currentTime.toFixed(2)}-${(currentTime + measureDurationSeconds).toFixed(2)}s)`);
      
      currentTime += measureDurationSeconds;
    }
    
    // Group notes by measures and calculate proper beat positions
    const notesByMeasure = new Map();
    
    allNotes.forEach(note => {
      for (const [measureNum, boundary] of measureBoundaries.entries()) {
        if (note.startTime >= boundary.startTime && note.startTime < boundary.endTime) {
          if (!notesByMeasure.has(measureNum)) {
            notesByMeasure.set(measureNum, []);
          }
          
          // Calculate beat position: (time_within_measure * BPM) / 60 + 1
          const timeWithinMeasure = note.startTime - boundary.startTime;
          const beatPosition = (timeWithinMeasure * (currentTempoBPMRef.current || 120)) / 60 + 1;
          
          notesByMeasure.get(measureNum).push({
            ...note,
            beatPosition: beatPosition,
            measureNum: measureNum,
            timeWithinMeasure: timeWithinMeasure
          });
          break;
        }
      }
    });
    
    // DIVISI DETECTION AND COMPREHENSIVE REPORTING
    // console.log('\n🎵 DIVISI ANALYSIS:');
    // console.log('==========================================');
    
    const allDivisiInstances = [];
    let totalDivisiCount = 0;
    
    // Analyze each measure for divisi
    for (let measure = 1; measure <= Math.min(200, osmd.Sheet.sourceMeasures.length); measure++) {
      const boundary = measureBoundaries.get(measure);
      const notes = notesByMeasure.get(measure) || [];
      
      if (notes.length === 0) continue;
      
      // Group by instrument ID (now partId is already the simple instrument ID)
      const notesByInstrument = new Map();
      notes.forEach(note => {
        const instrumentId = note.partId;
        if (!notesByInstrument.has(instrumentId)) {
          notesByInstrument.set(instrumentId, []);
        }
        notesByInstrument.get(instrumentId).push(note);
      });
      
      // Check each instrument for overlapping notes from different voices (divisi)
      for (const [instrumentId, instrumentNotes] of notesByInstrument.entries()) {
        // Debug analysis removed for clarity
        
        // Sort notes by start time
        instrumentNotes.sort((a, b) => a.startTime - b.startTime);
        
        // Check for divisi: multiple notes at the same beat (even within the same voice)
        for (let i = 0; i < instrumentNotes.length; i++) {
          for (let j = i + 1; j < instrumentNotes.length; j++) {
            const note1 = instrumentNotes[i];
            const note2 = instrumentNotes[j];
            
            // Skip rests
            if (note1.isRest || note2.isRest) continue;
            
            // Consider this divisi if:
            // 1. Notes are from different voices (different voiceIds) OR same voice with different pitches
            // 2. Notes start at the same beat position (within 0.1 beats)  
            // 3. Notes have different pitches
            const isDifferentVoice = note1.voiceId !== note2.voiceId;
            const hasDifferentPitch = note1.pitch !== note2.pitch;
            const beatDifference = Math.abs(note1.beatPosition - note2.beatPosition);
            const isSameBeat = beatDifference < 0.1; // Within 0.1 beats
            
            // Debug output removed for clarity
            
            if ((isDifferentVoice || hasDifferentPitch) && isSameBeat && hasDifferentPitch) {
              // Determine upper and lower voice based on pitch
              // Convert pitch to MIDI number for comparison
              const getMidiNumber = (pitch) => {
                try {
                  return Tone.Frequency(pitch).toMidi();
                } catch {
                  return 60; // Default to middle C if conversion fails
                }
              };
              
              const note1Midi = getMidiNumber(note1.pitch);
              const note2Midi = getMidiNumber(note2.pitch);
              
              const upperNote = note1Midi > note2Midi ? note1 : note2;
              const lowerNote = note1Midi > note2Midi ? note2 : note1;
              
              // Found divisi - logging removed for clarity
              
              // Calculate overlap details
              const overlapStart = Math.max(note1.startTime, note2.startTime);
              const overlapEnd = Math.min(note1.endTime, note2.endTime);
              const overlapDuration = overlapEnd - overlapStart;
              
              // Calculate beat positions
              const note1Beat = note1.beatPosition;
              const note2Beat = note2.beatPosition;
              
              // Calculate durations in beats
              const note1DurationBeats = ((note1.endTime - note1.startTime) * (currentTempoBPMRef.current || 120)) / 60;
              const note2DurationBeats = ((note2.endTime - note2.startTime) * (currentTempoBPMRef.current || 120)) / 60;
              const overlapDurationBeats = (overlapDuration * (currentTempoBPMRef.current || 120)) / 60;
              
              const divisiInstance = {
                measure: measure,
                partId: instrumentId,
                timeSignature: `${boundary.timeSignature.numerator}/${boundary.timeSignature.denominator}`,
                upperVoice: {
                  pitch: upperNote.pitch,
                  startBeat: upperNote.beatPosition,
                  durationBeats: ((upperNote.endTime - upperNote.startTime) * (currentTempoBPMRef.current || 120)) / 60,
                  startTime: upperNote.startTime,
                  endTime: upperNote.endTime,
                  sourceNote: upperNote
                },
                lowerVoice: {
                  pitch: lowerNote.pitch,
                  startBeat: lowerNote.beatPosition,
                  durationBeats: ((lowerNote.endTime - lowerNote.startTime) * (currentTempoBPMRef.current || 120)) / 60,
                  startTime: lowerNote.startTime,
                  endTime: lowerNote.endTime,
                  sourceNote: lowerNote
                },
                // Keep legacy note1/note2 for backward compatibility
                note1: {
                  pitch: note1.pitch,
                  startBeat: note1Beat,
                  durationBeats: note1DurationBeats,
                  startTime: note1.startTime,
                  endTime: note1.endTime
                },
                note2: {
                  pitch: note2.pitch,
                  startBeat: note2Beat,
                  durationBeats: note2DurationBeats,
                  startTime: note2.startTime,
                  endTime: note2.endTime
                },
                overlap: {
                  startTime: overlapStart,
                  endTime: overlapEnd,
                  durationSeconds: overlapDuration,
                  durationBeats: overlapDurationBeats
                }
              };
              
              allDivisiInstances.push(divisiInstance);
              totalDivisiCount++;
            }
          }
        }
      }
    }
    
    // Report all divisi instances
    // if (allDivisiInstances.length === 0) {
    //   console.log('No divisi detected in the first 10 measures.');
    // } else {
    //   console.log(`Found ${totalDivisiCount} divisi instances:`);
    //   console.log('');
    //   
    //   allDivisiInstances.forEach((instance, index) => {
    //     console.log(`${index + 1}. Measure ${instance.measure} (${instance.timeSignature}), Part ${instance.partId}:`);
    //     
    //     // Format beat positions
    //     const formatBeat = (beat) => {
    //       const wholeBeat = Math.floor(beat);
    //       const fraction = beat - wholeBeat;
    //       if (Math.abs(fraction) < 0.05) return `${wholeBeat}`;
    //       if (Math.abs(fraction - 0.5) < 0.05) return `${wholeBeat}.5`;
    //       if (Math.abs(fraction - 0.25) < 0.05) return `${wholeBeat}.25`;
    //       if (Math.abs(fraction - 0.75) < 0.05) return `${wholeBeat}.75`;
    //       return beat.toFixed(2);
    //     };
    //     
    //     // Format duration
    //     const formatDuration = (beats) => {
    //       if (Math.abs(beats - 1) < 0.05) return "1 beat";
    //       if (Math.abs(beats - 0.5) < 0.05) return "0.5 beats";
    //       if (Math.abs(beats - 2) < 0.05) return "2 beats";
    //       return `${beats.toFixed(2)} beats`;
    //     };
    //     
    //     console.log(`   Upper voice: ${instance.note1.pitch} (beat ${formatBeat(instance.note1.startBeat)}, ${formatDuration(instance.note1.durationBeats)})`);
    //     console.log(`   Lower voice: ${instance.note2.pitch} (beat ${formatBeat(instance.note2.startBeat)}, ${formatDuration(instance.note2.durationBeats)})`);
    //     console.log(`   Overlap: ${formatDuration(instance.overlap.durationBeats)} (${(instance.overlap.durationSeconds * 1000).toFixed(0)}ms)`);
    //     console.log('');
    //   });
    //   
    //   // Summary by part
    //   const divisiByPart = new Map();
    //   allDivisiInstances.forEach(instance => {
    //     if (!divisiByPart.has(instance.partId)) {
    //       divisiByPart.set(instance.partId, []);
    //     }
    //     divisiByPart.get(instance.partId).push(instance);
    //   });
    //   
    //   console.log('📊 DIVISI SUMMARY BY PART:');
    //   for (const [partId, instances] of divisiByPart.entries()) {
    //     const measures = [...new Set(instances.map(i => i.measure))].sort((a, b) => a - b);
    //     console.log(`Part ${partId}: ${instances.length} instances in measures ${measures.join(', ')}`);
    //   }
    // }
    // 
    // console.log('\n==========================================');
    
    // CROSS-REFERENCE DIVISI WITH PLAYBACK SYSTEM
    // console.log('\n🔗 LINKING DIVISI TO PLAYBACK SYSTEM:');
    // console.log('==========================================');
    
    // Create comprehensive note mapping for divisi instances
    const enhancedDivisiInstances = allDivisiInstances.map((instance, index) => {
      // Find corresponding notes in our playback systems
      const findPlaybackNote = (noteData) => {
        // Find in noteEvents (audio playback)
        const audioNote = noteEvents.find(event => 
          Math.abs(event.time - noteData.startTime) < 0.01 && 
          event.pitch === noteData.pitch &&
          event.partId === instance.partId
        );
        
        // Find in allNotesWithTiming (visual highlighting)
        const visualNote = allNotesWithTimingRef.current.find(note =>
          Math.abs(note.startTime - noteData.startTime) < 0.01 &&
          note.pitch === noteData.pitch &&
          note.partId === instance.partId
        );
        
        return { audioNote, visualNote };
      };
      
      const note1Refs = findPlaybackNote(instance.note1);
      const note2Refs = findPlaybackNote(instance.note2);
      
      return {
        ...instance,
        divisiId: `divisi_${index + 1}`,
        playbackReferences: {
          note1: {
            audioEvent: note1Refs.audioNote,
            visualEvent: note1Refs.visualNote,
            sourceNote: note1Refs.visualNote?.sourceNote
          },
          note2: {
            audioEvent: note2Refs.audioNote,
            visualEvent: note2Refs.visualEvent,
            sourceNote: note2Refs.visualNote?.sourceNote
          }
        },
        // Add helper methods for playback integration
        getAudioEvents: () => [note1Refs.audioNote, note2Refs.audioNote].filter(Boolean),
        getVisualEvents: () => [note1Refs.visualNote, note2Refs.visualNote].filter(Boolean),
        getSourceNotes: () => [note1Refs.visualNote?.sourceNote, note2Refs.visualNote?.sourceNote].filter(Boolean)
      };
    });
    
    // Store enhanced divisi data for use by other systems
    osmdRef.current._divisiAnalysis = {
      instances: enhancedDivisiInstances,
      totalCount: totalDivisiCount,
      measureBoundaries: measureBoundaries,
      timeSignatures: timeSignatures,
      
      // Helper methods for integration
      getDivisiInMeasure: (measureNum) => enhancedDivisiInstances.filter(d => d.measure === measureNum),
      getDivisiInPart: (partId) => enhancedDivisiInstances.filter(d => d.partId === partId),
      getDivisiAtTime: (timeSeconds, tolerance = 0.1) => 
        enhancedDivisiInstances.filter(d => 
          timeSeconds >= d.overlap.startTime - tolerance && 
          timeSeconds <= d.overlap.endTime + tolerance
        ),
      
      // Get all notes involved in divisi for special highlighting
      getAllDivisiNotes: () => {
        const divisiNotes = new Set();
        enhancedDivisiInstances.forEach(instance => {
          instance.getVisualEvents().forEach(note => divisiNotes.add(note));
        });
        return Array.from(divisiNotes);
      }
    };
    
    // Divisi analysis complete - logging removed for clarity
    
    // Report the cross-referencing results
    const foundAudioRefs = enhancedDivisiInstances.filter(d => 
      d.playbackReferences.note1.audioEvent && d.playbackReferences.note2.audioEvent
    ).length;
    const foundVisualRefs = enhancedDivisiInstances.filter(d => 
      d.playbackReferences.note1.visualEvent && d.playbackReferences.note2.visualEvent
    ).length;
    
    // Debug the visual linking issue
    // console.log('\n🔍 DEBUGGING VISUAL LINKS:');
    // enhancedDivisiInstances.forEach((d, i) => {
    //   console.log(`Divisi ${i+1}:`, {
    //     note1Visual: !!d.playbackReferences.note1.visualEvent,
    //     note2Visual: !!d.playbackReferences.note2.visualEvent,
    //     note1Pitch: d.note1.pitch,
    //     note2Pitch: d.note2.pitch,
    //     measure: d.measure,
    //     partId: d.partId
    //   });
    // });
    
    // console.log(`✓ Linked ${foundAudioRefs}/${totalDivisiCount} divisi instances to audio playback events`);
    // console.log(`✓ Linked ${foundVisualRefs}/${totalDivisiCount} divisi instances to visual highlighting events`);
    // console.log(`✓ Created comprehensive divisi database with ${enhancedDivisiInstances.length} enhanced instances`);
    
  }, []);

  // Apply divisi filtering to note events after divisi analysis is complete
  const applyDivisiFiltering = useCallback((noteEvents, partControls) => {
    if (!osmdRef.current?._divisiAnalysis) {
      return noteEvents;
    }
    
    const divisiAnalysis = osmdRef.current._divisiAnalysis;
    
    const filtered = noteEvents.filter(noteEvent => {
      // Convert audio noteEvent format to format expected by shouldIncludeNoteForAudio
      const adaptedEvent = {
        ...noteEvent,
        startTime: noteEvent.time, // shouldIncludeNoteForAudio expects startTime, but noteEvents use time
      };
      const shouldInclude = shouldIncludeNoteForAudio(adaptedEvent, partControls, divisiAnalysis, partToInstrumentMapRef);
      
      // Debug: Compare what audio sees vs highlighting for same note  
      if (noteEvent.pitch === 'A#3' && Math.abs(noteEvent.time - 6.75) < 0.1) {
        // console.log(`🔍 AUDIO NOTE:`, {
        //   pitch: noteEvent.pitch,
        //   time: noteEvent.time,
        //   partId: noteEvent.partId,
        //   shouldInclude: shouldInclude
        // });
      }
      
      return shouldInclude;
    });
    
    return filtered;
  }, []);

  // Apply divisi filtering to highlighting notes as well  
  const applyDivisiFilteringToHighlighting = useCallback((allNotesWithTiming, ignoredPartControls) => {
    // Use current partControls from ref instead of parameter to avoid stale closures
    const partControls = partControlsRef.current;
    
    // Filter debug removed
    if (!osmdRef.current?._divisiAnalysis) {
      return allNotesWithTiming;
    }
    
    const divisiAnalysis = osmdRef.current._divisiAnalysis;
    
    const originalCount = allNotesWithTiming.length;
    const filtered = [];
    allNotesWithTiming.forEach(noteEvent => {
      // Skip rests - they should always be highlighted
      if (noteEvent.isRest) {
        filtered.push(noteEvent);
        return;
      }
      
      // Convert highlighting note format to audio note format for filtering
      const audioFormatNote = {
        time: noteEvent.startTime, // highlighting uses startTime, audio uses time
        pitch: noteEvent.pitch,
        partId: noteEvent.partId,
        startTime: noteEvent.startTime
      };
      
      const shouldInclude = shouldIncludeNoteForAudio(audioFormatNote, partControls, divisiAnalysis, partToInstrumentMapRef);
      
      // Debug divisi notes specifically
      if (noteEvent.pitch === 'F3' || noteEvent.pitch === 'A#3') {
        // console.log(`🔍 HIGHLIGHTING: ${noteEvent.pitch} at ${noteEvent.startTime.toFixed(2)}s - shouldInclude: ${shouldInclude}`);
        // console.log(`   partId: ${noteEvent.partId}`);
        const partControl = partControls.find(p => p.id.toString() === noteEvent.partId.toString());
        if (partControl) {
          // console.log(`   found partControl: upperVoice=${partControl.upperVoice}, lowerVoice=${partControl.lowerVoice}`);
        } else {
          // console.log(`   NO partControl found for partId ${noteEvent.partId}`);
          // console.log(`   available partControls:`, partControls.map(p => ({id: p.id, upperVoice: p.upperVoice, lowerVoice: p.lowerVoice})));
        }
      }
      
      if (shouldInclude) {
        filtered.push(noteEvent);
      }
    });
    
    // Filter result debug removed
    return filtered;
  }, []);

  const handlePartToggle = useCallback((instrumentId, checked) => {
    // Find all part IDs associated with this instrument
    const associatedParts = [];
    for (const [partId, instrId] of Object.entries(partToInstrumentMapRef.current)) {
      if (String(instrId) === String(instrumentId)) {
        associatedParts.push(partId);
      }
    }
    
    // Update UI state
    setPartControls(prev => prev.map(part => 
      part.id === instrumentId ? { ...part, checked } : part
    ));
    
    
    return associatedParts;
  }, []);

  // New function to handle visibility toggling
  // Batched visibility update to prevent multiple re-renders
  const handlePartVisibilityBatch = useCallback((updates, measureHighlighterRef, noteHighlighterRef, renderCoordinator = null) => {
    // Update UI state in a single batch and capture the new state
    let updatedPartControls;
    setPartControls(prev => {
      updatedPartControls = prev.map(part => {
        const update = updates.find(u => u.id === part.id);
        return update ? { ...part, visible: update.visible } : part;
      });
      return updatedPartControls;
    });
    
    // Keep ref in sync with the updated state immediately for settings loading
    partControlsRef.current = updatedPartControls;
    
    // Update OSMD visibility
    if (osmdRef.current?.Sheet?.Instruments) {
      updates.forEach(({ id, visible }) => {
        const instrument = osmdRef.current.Sheet.Instruments.find(inst => inst.Id === id);
        if (instrument) {
          instrument.Visible = visible;
        }
      });
      
      // Clear note highlights before re-rendering to prevent orphaned highlights
      if (noteHighlighterRef?.current) {
        noteHighlighterRef.current.resetAfterDOMChange();
      }
      
      // Use render coordinator if available, otherwise fall back to direct render
      if (renderCoordinator) {
        // Set up one-time completion callback for post-render updates
        if (osmdRef.current) {
          // Clear any existing callback to prevent conflicts
          osmdRef.current._onRenderComplete = null;
          
          // Create a one-time callback that clears itself after execution
          osmdRef.current._onRenderComplete = () => {
            try {
              // Update measure highlighter visibility after re-render - use updated state
              if (measureHighlighterRef?.current) {
                measureHighlighterRef.current.updatePartVisibility(updatedPartControls);
              }
              
              // Reset note highlighter's internal state after DOM changes
              if (noteHighlighterRef?.current) {
                noteHighlighterRef.current.resetAfterDOMChange();
              }
            } finally {
              // Clear the callback after execution to prevent interference with subsequent operations
              if (osmdRef.current) {
                osmdRef.current._onRenderComplete = null;
              }
            }
          };
        }
        
        // Request coordinated render for part visibility changes (use normal priority for batching)
        renderCoordinator.requestRender({ 
          priority: 'normal', 
          reason: 'part-visibility-batch' 
        });
      } else {
        // Fallback to direct render (legacy behavior)
        const originalConsoleError = console.error;
        console.error = (msg, ...args) => {
          if (typeof msg === 'string' && msg.includes('attribute width: A negative value')) {
            return; // Suppress SVG width errors
          }
          originalConsoleError(msg, ...args);
        };
        
        try {
          osmdRef.current.render().then(() => {
            // Update measure highlighter visibility after re-render - use updated state
            if (measureHighlighterRef?.current) {
              measureHighlighterRef.current.updatePartVisibility(updatedPartControls);
            }
            
            // Reset note highlighter's internal state after DOM changes
            if (noteHighlighterRef?.current) {
              noteHighlighterRef.current.resetAfterDOMChange();
            }
          });
        } catch (error) {
          // Fallback if render fails
        } finally {
          console.error = originalConsoleError;
        }
      }
    }
  }, []);

  const handlePartVisibilityToggle = useCallback((instrumentId, visible, measureHighlighterRef, noteHighlighterRef, renderCoordinator = null) => {
    // Use batched update for single changes too
    handlePartVisibilityBatch([{ id: instrumentId, visible }], measureHighlighterRef, noteHighlighterRef, renderCoordinator);
  }, [handlePartVisibilityBatch]);

  const handleUpperVoiceToggle = useCallback((instrumentId, enabled, audioHook) => {
    // console.log(`🔧 Upper voice toggle: instrument ${instrumentId} → ${enabled}`);
    const newPartControls = partControlsRef.current.map(part => 
      part.id === instrumentId ? { ...part, upperVoice: enabled } : part
    );
    setPartControls(newPartControls);
    partControlsRef.current = newPartControls; // Keep ref in sync
    
    // Force highlighting to refresh by clearing cache and highlights
    if (osmdRef.current?._lastHighlightTime !== undefined) {
      delete osmdRef.current._lastHighlightTime;
    }
    
    // Re-prepare playback to apply divisi filtering to audio events
    if (osmdRef.current && audioHook) {
      // Clear any existing debounce timeout
      if (osmdRef.current._preparingPlaybackTimeout) {
        clearTimeout(osmdRef.current._preparingPlaybackTimeout);
      }
      
      // Debounce preparation calls
      osmdRef.current._preparingPlaybackTimeout = setTimeout(() => {
        if (osmdRef.current && audioHook) {
          preparePlayback(osmdRef.current, audioHook);
        }
        osmdRef.current._preparingPlaybackTimeout = null;
      }, 100);
    }
  }, [preparePlayback]);

  const handleLowerVoiceToggle = useCallback((instrumentId, enabled, audioHook) => {
    // console.log(`🔧 Lower voice toggle: instrument ${instrumentId} → ${enabled}`);
    const newPartControls = partControlsRef.current.map(part => 
      part.id === instrumentId ? { ...part, lowerVoice: enabled } : part
    );
    setPartControls(newPartControls);
    partControlsRef.current = newPartControls; // Keep ref in sync
    
    // Force highlighting to refresh by clearing cache and highlights
    if (osmdRef.current?._lastHighlightTime !== undefined) {
      delete osmdRef.current._lastHighlightTime;
    }
    
    // Re-prepare playback to apply divisi filtering to audio events
    if (osmdRef.current && audioHook) {
      // Clear any existing debounce timeout
      if (osmdRef.current._preparingPlaybackTimeout) {
        clearTimeout(osmdRef.current._preparingPlaybackTimeout);
      }
      
      // Debounce preparation calls
      osmdRef.current._preparingPlaybackTimeout = setTimeout(() => {
        if (osmdRef.current && audioHook) {
          preparePlayback(osmdRef.current, audioHook);
        }
        osmdRef.current._preparingPlaybackTimeout = null;
      }, 100);
    }
  }, [preparePlayback]);

  // Auto-sync visibility when parts are ready and sync is pending
  useEffect(() => {
    if (partControls.length > 0 && pendingVisibilitySync && osmdRef.current?.Sheet?.Instruments) {
      // Check if any parts need to be hidden
      const hasHiddenParts = partControls.some(part => part.visible === false);
      
      if (hasHiddenParts) {
        try {
          // Set visibility for all instruments based on current UI state
          osmdRef.current.Sheet.Instruments.forEach(instrument => {
            const currentPart = partControls.find(part => part.id === instrument.Id);
            if (currentPart) {
              instrument.Visible = currentPart.visible !== false;
            }
          });
          
          // Re-render to apply visibility changes with error suppression
          const originalConsoleError = console.error;
          console.error = (msg, ...args) => {
            if (typeof msg === 'string' && msg.includes('attribute width: A negative value')) {
              return; // Suppress SVG width errors
            }
            originalConsoleError(msg, ...args);
          };
          
          try {
            osmdRef.current.render();
          } finally {
            console.error = originalConsoleError; // Restore console.error
          }
          
          // Restore user's actual visibility preferences (not just all visible)
          if (partControlsRef.current) {
            osmdRef.current.Sheet.Instruments.forEach(instrument => {
              const partControl = partControlsRef.current.find(part => part.id === instrument.Id);
              instrument.Visible = partControl ? partControl.visible !== false : true;
            });
          }
        } catch (error) {
        }
      }
      
      // Clear the pending flag and signal completion
      setPendingVisibilitySync(false);
      
      // Notify that initialization is complete
      if (osmdRef.current._onComplete) {
        osmdRef.current._onComplete();
        osmdRef.current._onComplete = null; // Clear to prevent double calls
      }
    }
  }, [partControls, pendingVisibilitySync]);

  // Function to sync OSMD visibility with current UI state
  const syncVisibilityWithUI = useCallback((measureHighlighterRef, noteHighlighterRef, renderCoordinator = null) => {
    
    if (partControls.length > 0) {
      // Parts are ready, sync immediately
      const hasHiddenParts = partControls.some(part => part.visible === false);
      
      if (hasHiddenParts && osmdRef.current?.Sheet?.Instruments) {
        try {
          // Clear note highlights before making visibility changes
          if (noteHighlighterRef?.current) {
            noteHighlighterRef.current.resetAfterDOMChange();
          }
          
          osmdRef.current.Sheet.Instruments.forEach(instrument => {
            const currentPart = partControls.find(part => part.id === instrument.Id);
            if (currentPart) {
              instrument.Visible = currentPart.visible !== false;
            }
          });
          
          // Use render coordinator if available, otherwise fall back to direct render
          if (renderCoordinator) {
            // Set up completion callback for post-render updates
            if (osmdRef.current) {
              osmdRef.current._onRenderComplete = () => {
                // CRITICAL: Refresh measure highlighter after sync
                if (measureHighlighterRef?.current) {
                  measureHighlighterRef.current.updatePartVisibility(partControls);
                  setTimeout(() => {
                    measureHighlighterRef.current.refreshAfterLayoutChange();
                  }, 100);
                }
                
                // Clear note highlights again after render to ensure clean state
                if (noteHighlighterRef?.current) {
                  setTimeout(() => {
                    noteHighlighterRef.current.resetAfterDOMChange();
                  }, 50);
                }
                
                // Restore user's actual visibility preferences (not just all visible)
                if (partControlsRef.current) {
                  osmdRef.current.Sheet.Instruments.forEach(instrument => {
                    const partControl = partControlsRef.current.find(part => part.id === instrument.Id);
                    instrument.Visible = partControl ? partControl.visible !== false : true;
                  });
                }
              };
            }
            
            // Request immediate render for sync operations (needs to be responsive)
            renderCoordinator.requestRender({ 
              priority: 'immediate', 
              reason: 'visibility-sync' 
            });
          } else {
            // Fallback to direct render (legacy behavior)
            osmdRef.current.render();
            
            // CRITICAL: Refresh measure highlighter after sync
            if (measureHighlighterRef?.current) {
              measureHighlighterRef.current.updatePartVisibility(partControls);
              setTimeout(() => {
                measureHighlighterRef.current.refreshAfterLayoutChange();
              }, 100);
            }
            
            // Clear note highlights again after render to ensure clean state
            if (noteHighlighterRef?.current) {
              setTimeout(() => {
                noteHighlighterRef.current.resetAfterDOMChange();
              }, 50);
            }
            
            // Restore user's actual visibility preferences (not just all visible)
            if (partControlsRef.current) {
              osmdRef.current.Sheet.Instruments.forEach(instrument => {
                const partControl = partControlsRef.current.find(part => part.id === instrument.Id);
                instrument.Visible = partControl ? partControl.visible !== false : true;
              });
            }
          }
        } catch (error) {
          console.error('Error syncing visibility with UI:', error);
        }
      }
    } else {
      // Parts not ready yet, mark as pending
      setPendingVisibilitySync(true);
    }
  }, [partControls]);

  const getCurrentNotes = useCallback((currentTime) => {
    const currentNotes = {};
    
    // Initialize all parts as REST
    partControls.forEach(part => {
      currentNotes[part.id] = 'REST';
    });
    
    // Find notes playing at current time
    allNotesWithTimingRef.current.forEach(noteEvent => {
      if (currentTime >= noteEvent.startTime && currentTime < noteEvent.endTime) {
        // Apply divisi filtering for highlighting
        if (!shouldIncludeNoteForAudio(noteEvent, partControls, osmdRef.current?._divisiAnalysis, partToInstrumentMapRef)) {
          return; // Skip this note if it's filtered out
        }
        
        // Map part ID to instrument ID for display
        const instrumentId = partToInstrumentMapRef.current[noteEvent.partId] || noteEvent.partId;
        if (noteEvent.isRest) {
          currentNotes[instrumentId] = 'REST';
        } else {
          // If multiple notes are playing simultaneously, show them all
          if (currentNotes[instrumentId] === 'REST') {
            currentNotes[instrumentId] = noteEvent.pitch;
          } else if (currentNotes[instrumentId] !== noteEvent.pitch) {
            currentNotes[instrumentId] += `, ${noteEvent.pitch}`;
          }
        }
      }
    });
    
    // REAL-TIME DIVISI DETECTION AND ANNOUNCEMENT
    if (osmdRef.current?._divisiAnalysis) {
      const activeDivisi = osmdRef.current._divisiAnalysis.getDivisiAtTime(currentTime, 0.05);
      
      // Initialize loop counter and tracking
      if (!osmdRef.current._loopIteration) {
        osmdRef.current._loopIteration = 0;
      }
      if (!osmdRef.current._lastDivisiAnnouncements) {
        osmdRef.current._lastDivisiAnnouncements = new Set();
      }
      if (!osmdRef.current._lastLoopTime) {
        osmdRef.current._lastLoopTime = currentTime;
      }
      
      // Detect loop restart (time goes backwards or jumps to beginning)
      if (currentTime < osmdRef.current._lastLoopTime - 1.0 || (osmdRef.current._lastLoopTime > 5.0 && currentTime < 2.0)) {
        osmdRef.current._loopIteration++;
        osmdRef.current._lastDivisiAnnouncements.clear();
      }
      osmdRef.current._lastLoopTime = currentTime;
      
      activeDivisi.forEach(divisi => {
        // Create unique key with loop iteration to allow announcements in each loop
        const announceKey = `${divisi.divisiId}_${Math.floor(currentTime)}_loop${osmdRef.current._loopIteration}`;
        
        if (!osmdRef.current._lastDivisiAnnouncements.has(announceKey)) {
          osmdRef.current._lastDivisiAnnouncements.add(announceKey);
        }
      });
    }
    
    return currentNotes;
  }, [partControls, partToInstrumentMapRef, allNotesWithTimingRef]);

  // Export the original BPM for the speed controls
  const getOriginalBPM = useCallback(() => {
    return currentTempoBPMRef.current;
  }, []);

  // Toggle repeat respect and re-prepare playback
  const toggleRespectRepeats = useCallback(async (audioHook) => {
    const newRespectRepeats = !respectRepeats;
    setRespectRepeats(newRespectRepeats);
    
    // Re-prepare playback with new repeat setting
    if (osmdRef.current && audioHook) {
      await preparePlayback(osmdRef.current, audioHook);
    }
  }, [respectRepeats, preparePlayback]);

  // Get repeat analysis information
  const getRepeatAnalysis = useCallback(() => {
    return repeatAnalysisRef.current;
  }, []);

  return {
    partControls,
    setPartControls,
    partControlsRef,
    instrumentToPartMapRef,
    partToInstrumentMapRef,
    allNotesWithTimingRef,
    originalNotesWithTimingRef,
    originalNoteEventsRef,
    createPartControls,
    preparePlayback,
    handlePartToggle,
    handlePartVisibilityToggle,
    handlePartVisibilityBatch,
    handleUpperVoiceToggle,
    handleLowerVoiceToggle,
    syncVisibilityWithUI,
    getCurrentNotes,
    getOriginalBPM,
    applyDivisiFilteringToHighlighting,
    // Repeat functionality
    respectRepeats,
    setRespectRepeats,
    toggleRespectRepeats,
    getRepeatAnalysis,
    // Swing detection
    detectSwing
  };
};